/**
 * Teams dev-channel notification utility.
 *
 * Instead of calling the Power Automate webhook directly from the browser
 * (which would expose the webhook URL), the frontend calls the MMA backend
 * endpoint POST /v1/notify-dev-channel.
 *
 * The backend:
 *  - Reads TEAMS_WEBHOOK_URL (secret, never sent to the browser)
 *  - Forwards the full payload to the Power Automate workflow
 *  - Returns { teams_bug_report_channel_url } for the "Switch to Teams" deep-link
 */

export interface TeamsErrorPayload {
    error_id?: string
    error?: string
    status?: number | string
    message?: string
    recoveryMechanism?: string
    userDescription?: string
    reporterName?: string
    reporterEmail?: string
    sessionId?: string
    details?: Record<string, unknown>
    [key: string]: unknown
}

/**
 * Thrown when the /v1/shareToDev endpoint is unreachable (network error,
 * 404, or gateway-level failures like 502/503/504). The component catches
 * this to render a Teams share button via the launcher.js SDK as a fallback.
 */
export class TeamsEndpointUnavailableError extends Error {
    constructor(cause?: Error) {
        super('Teams endpoint unavailable')
        this.name = 'TeamsEndpointUnavailableError'
        if (cause) this.cause = cause
    }
}

/**
 * Posts an error / bug report to the MMA backend, which forwards it to the
 * configured Teams Power Automate webhook and returns the channel deep-link.
 *
 * @param backendEndpoint  - Base URL of the MMA backend (e.g. "http://localhost:8000/v1")
 * @param errorPayload     - The structured error payload (reporter info + error details)
 * @returns                - The Teams channel URL for the "Switch to Teams" button,
 *                           or an empty string when the backend has no webhook configured.
 * @throws TeamsEndpointUnavailableError when the endpoint cannot be reached
 */

export const sendTeamsNotification = async (
    backendEndpoint: string,
    space: string,
    errorPayload: TeamsErrorPayload,
    accessToken?: string,
    userId?: string,
): Promise<string> => {
    const errorId = String(errorPayload.error_id ?? '')
    const errorCode = String(errorPayload.error ?? '')
    const httpStatus = String(errorPayload.status ?? '')
    const userDescription = String(errorPayload.userDescription ?? '').trim()

    const message = userDescription || [errorCode, httpStatus ? `HTTP ${httpStatus}` : ''].filter(Boolean).join(' · ') || 'MMA error report'
    const sessionId = errorPayload.sessionId || '';

    // reporter_name, reporter_email and session_url are derived server-side
    // from the auth token and CORS origins — never sent from the client.
    const body = {
        message,
        error_id: errorId,
        session_id: sessionId,
    }

    // Strip trailing /v1 if present – the endpoint is registered at /v1/spaces/{space}/shareToDev
    const base = backendEndpoint.replace(/\/v1\/?$/, '')
    const query = userId ? `?userID=${encodeURIComponent(userId)}` : ''

    let response: Response
    try {
        response = await fetch(`${base}/v1/spaces/${space}/shareToDev${query}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
            body: JSON.stringify(body),
        })
    } catch (err) {
        // Network-level failure (no connection, DNS error, CORS block, etc.)
        throw new TeamsEndpointUnavailableError(err instanceof Error ? err : undefined)
    }

    if (!response.ok) {
        // Treat endpoint-absent and gateway failures as unavailable
        if (response.status === 404 || response.status >= 502) {
            throw new TeamsEndpointUnavailableError()
        }

        let errorBody = `Backend /shareToDev responded with ${response.status}: ${response.statusText}`
        try {
            const json = await response.json()
            if (json?.detail) errorBody = JSON.stringify(json)
        } catch {
            // Non-JSON response — keep default message
        }
        throw new Error(errorBody)
    }

    const result = await response.json()
    return (result?.teams_bug_report_channel_url as string) ?? ''
}
